(() => {
  "use strict";

  const normalize = (value) =>
    String(value || "")
      .toLocaleLowerCase("de")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const slugify = (value) =>
    normalize(value)
      .replace(/&/g, " und ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const directChildren = (element, selector) =>
    [...element.children].filter((child) => child.matches(selector));

  const markLegacyNavigation = (body) => {
    const candidate = body.firstElementChild;
    if (!candidate) return;
    const links = [...candidate.querySelectorAll(":scope > a")];
    if (
      links.length >= 3 &&
      links.some((link) => link.getAttribute("href")?.includes("/pages/futterwissen")) &&
      links.some((link) => link.getAttribute("href")?.includes("/pages/futterlexikon"))
    ) {
      candidate.classList.add("llw-legacy-nav");
    }
  };

  const enhanceHero = (hero, tags) => {
    hero.classList.add("llw-hero");
    const heading = hero.querySelector("h1");
    const paragraphs = [...hero.children].filter((child) => child.tagName === "P");
    const eyebrow = paragraphs.find((paragraph) => heading && paragraph.compareDocumentPosition(heading) & Node.DOCUMENT_POSITION_FOLLOWING);
    const lead = paragraphs.find((paragraph) => heading && paragraph.compareDocumentPosition(heading) & Node.DOCUMENT_POSITION_PRECEDING);

    eyebrow?.classList.add("llw-hero__eyebrow");
    lead?.classList.add("llw-hero__lead");

    if (!tags.length || hero.querySelector(".llw-hero__tags")) return;
    const tagList = document.createElement("div");
    tagList.className = "llw-hero__tags";
    tagList.setAttribute("aria-label", "Das zeichnet diesen Bereich aus");
    for (const label of tags) {
      const tag = document.createElement("span");
      tag.textContent = label;
      tagList.append(tag);
    }
    hero.append(tagList);
  };

  const buildHeroFromDirectChildren = (body, tags) => {
    const heading = directChildren(body, "h1")[0];
    if (!heading) return null;
    const eyebrow = heading.previousElementSibling?.tagName === "P" ? heading.previousElementSibling : null;
    const lead = heading.nextElementSibling?.tagName === "P" ? heading.nextElementSibling : null;
    const hero = document.createElement("header");
    body.insertBefore(hero, eyebrow || heading);
    if (eyebrow) hero.append(eyebrow);
    hero.append(heading);
    if (lead) hero.append(lead);
    enhanceHero(hero, tags);
    return hero;
  };

  const groupTopics = (body) => {
    const headings = directChildren(body, "h2");
    const sections = [];

    for (const heading of headings) {
      const section = document.createElement("section");
      section.className = "llw-topic";
      section.id = `llw-${slugify(heading.textContent)}`;
      heading.parentNode.insertBefore(section, heading);
      section.append(heading);

      while (section.nextElementSibling && section.nextElementSibling.tagName !== "H2") {
        section.append(section.nextElementSibling);
      }
      sections.push(section);
    }
    return sections;
  };

  const addTopicNavigation = (body, sections, afterElement) => {
    if (!sections.length) return null;
    const nav = document.createElement("nav");
    nav.className = "llw-topic-nav";
    nav.setAttribute("aria-label", "Direkt zum Bereich");

    for (const section of sections) {
      const heading = section.querySelector(":scope > h2");
      if (!heading) continue;
      const link = document.createElement("a");
      link.href = `#${section.id}`;
      link.textContent = heading.textContent.replace(/^\s*\d+\.\s*/, "").trim();
      nav.append(link);
    }

    afterElement.insertAdjacentElement("afterend", nav);
    return nav;
  };

  const enhanceFutterwissen = (body) => {
    const hero = body.firstElementChild;
    if (hero) {
      enhanceHero(hero, ["Chargenbezogen gedacht", "Laborwerte verständlich", "Für die Praxis"]);
    }

    const grid = hero?.nextElementSibling;
    if (grid) {
      grid.classList.add("llw-hub-grid");
      if (!grid.querySelector("[data-llw-generated-card]")) {
        const additionalCards = [
          {
            number: "05",
            title: "Daten richtig lesen",
            text: "Bezugsbasis, Messgrenzen, Probenahme und Freigabestatus: So erkennst du, wie belastbar und vergleichbar eine Angabe ist.",
            label: "Datenqualität verstehen →",
            href: "/pages/futterlexikon#llw-prufung-datenqualitat-und-freigabe",
            tone: "pale"
          },
          {
            number: "06",
            title: "Vom Feld bis zur Charge",
            text: "Fläche, Pflanzenbestand, Ernte, Prüfung und Analyse: Wir zeigen, wie die Angaben einer Charge zusammenhängen.",
            label: "Rückverfolgbarkeit ansehen →",
            href: "/pages/futterlexikon#llw-charge-herkunft-und-ruckverfolgbarkeit",
            tone: "sand"
          }
        ];

        for (const item of additionalCards) {
          const card = document.createElement("a");
          card.href = item.href;
          card.className = `llw-hub-card llw-hub-card--${item.tone}`;
          card.dataset.llwGeneratedCard = "true";
          card.innerHTML = `
            <span>${item.number}</span>
            <h2>${item.title}</h2>
            <p>${item.text}</p>
            <strong>${item.label}</strong>
          `;
          grid.append(card);
        }
      }
      [...grid.children].forEach((card) => card.classList.add("llw-hub-card"));
    }

    const principle = grid?.nextElementSibling;
    principle?.classList.add("llw-principle");

    if (principle && !body.querySelector(".llw-system-map")) {
      const map = document.createElement("section");
      map.className = "llw-system-map";
      map.innerHTML = `
        <div class="llw-system-map__intro">
          <p><strong>So hängen unsere Angaben zusammen</strong></p>
          <h2>Von der Fläche bis zur verständlichen Einordnung.</h2>
          <p>Eine einzelne Zahl erklärt noch kein Futter. Erst Herkunft, Chargenmerkmale, Prüfungen und freigegebene Analysen ergeben gemeinsam ein nachvollziehbares Bild.</p>
        </div>
        <div class="llw-system-map__steps">
          <a href="/pages/futterlexikon#erzeugungsflaeche"><span>01</span><strong>Fläche & Pflanzenbestand</strong><p>Woher das Futter stammt und welche Pflanzenbasis dokumentiert ist.</p></a>
          <a href="/pages/futterlexikon#chargennummer"><span>02</span><strong>Ernte & Charge</strong><p>Welche Ernte eindeutig zu dieser angebotenen Charge gehört.</p></a>
          <a href="/pages/futterlexikon#chargenpruefung"><span>03</span><strong>Prüfung & Analyse</strong><p>Welche Befunde und Laborwerte tatsächlich für diese Charge vorliegen.</p></a>
          <a href="/pages/lollenstein-kriterien"><span>04</span><strong>Einordnung & Freigabe</strong><p>Was die Daten bedeuten und welche Aussage öffentlich freigegeben ist.</p></a>
        </div>
        <p class="llw-system-map__promise"><strong>Unser Versprechen:</strong> Was wir wissen, zeigen wir. Was noch nicht belegt oder freigegeben ist, kennzeichnen wir offen.</p>
      `;
      principle.insertAdjacentElement("afterend", map);
    }
  };

  const enhanceLexicon = (body) => {
    markLegacyNavigation(body);
    const totalTerms = body.querySelectorAll("details[id], div[id]").length;
    const hero = buildHeroFromDirectChildren(body, [
      `${totalTerms} Begriffe verständlich erklärt`,
      "Direkt am Produkt verlinkt",
      "Messwert und Einordnung getrennt"
    ]);
    const sections = groupTopics(body);
    const topicNavigation = addTopicNavigation(body, sections, hero);

    for (const section of sections) {
      for (const group of directChildren(section, "div")) {
        const details = directChildren(group, "details");
        if (details.length) {
          group.classList.add("llw-details-grid");
          details.forEach((detail) => detail.classList.add("llw-term-card"));
          continue;
        }

        const cards = directChildren(group, "div[id]");
        if (cards.length) {
          group.classList.add("llw-mini-grid");
          cards.forEach((card) => card.classList.add("llw-mini-card"));
        }
      }
    }

    const items = [...body.querySelectorAll("details[id], .llw-mini-card[id]")];
    if (!items.length || !topicNavigation) return;

    const search = document.createElement("div");
    search.className = "llw-search";
    search.innerHTML = `
      <div class="llw-search__field">
        <label for="llw-lexicon-search">Begriff suchen</label>
        <input id="llw-lexicon-search" type="search" inputmode="search" autocomplete="off" placeholder="Zum Beispiel Rohfaser, Fruktan oder Haptik">
        <span class="llw-search__icon" aria-hidden="true">⌕</span>
      </div>
      <div class="llw-search__count" aria-live="polite"></div>
      <p class="llw-search__empty" hidden>Zu dieser Suche wurde kein Begriff gefunden. Probiere ein kürzeres Wort.</p>
    `;
    topicNavigation.insertAdjacentElement("afterend", search);

    const input = search.querySelector("input");
    const count = search.querySelector(".llw-search__count");
    const empty = search.querySelector(".llw-search__empty");
    const searchable = items.map((item) => ({ item, haystack: normalize(item.textContent) }));

    const update = () => {
      const query = normalize(input.value);
      let visibleCount = 0;
      for (const entry of searchable) {
        const visible = !query || entry.haystack.includes(query);
        entry.item.hidden = !visible;
        if (visible) visibleCount += 1;
      }

      for (const section of sections) {
        const sectionItems = items.filter((item) => section.contains(item));
        section.hidden = Boolean(query) && sectionItems.length > 0 && sectionItems.every((item) => item.hidden);
        for (const group of section.querySelectorAll(".llw-details-grid, .llw-mini-grid")) {
          const groupItems = [...group.children].filter((child) => child.matches("details[id], .llw-mini-card[id]"));
          group.hidden = Boolean(query) && groupItems.length > 0 && groupItems.every((item) => item.hidden);
          const precedingHeading = group.previousElementSibling;
          if (precedingHeading?.tagName === "H3") precedingHeading.hidden = group.hidden;
        }
      }

      count.textContent = query ? `${visibleCount} von ${items.length} Begriffen` : `${items.length} Begriffe`;
      empty.hidden = visibleCount !== 0;
    };

    input.addEventListener("input", update);
    input.addEventListener("search", update);
    update();

    const openHashTarget = () => {
      const id = decodeURIComponent(location.hash.slice(1));
      if (!id) return;
      const target = document.getElementById(id);
      if (target?.tagName === "DETAILS") target.open = true;
    };
    openHashTarget();
    window.addEventListener("hashchange", openHashTarget);
  };

  const ratingTone = (title) => {
    const key = normalize(title);
    if (key.includes("sehr gut")) return "positive";
    if (key.includes("gut geeignet")) return "good";
    if (key.includes("bedingt")) return "warning";
    if (key.includes("nicht empfohlen")) return "critical";
    return "neutral";
  };

  const enhanceCriteria = (body) => {
    markLegacyNavigation(body);
    const hero = buildHeroFromDirectChildren(body, [
      "Konkrete Erntecharge",
      "Nachvollziehbare Begründung",
      "Nur freigegebene Einordnungen"
    ]);

    const principle = [...directChildren(body, "div")].find((element) =>
      normalize(element.textContent).startsWith("unser grundprinzip")
    );
    principle?.classList.add("llw-dark-callout");

    const sections = groupTopics(body);

    for (const section of sections) {
      const heading = section.querySelector(":scope > h2")?.textContent.trim() || "";
      const key = normalize(heading);

      if (key.startsWith("1.")) {
        const grid = directChildren(section, "div")[0];
        grid?.classList.add("llw-data-grid");
        [...(grid?.children || [])].forEach((card, index) => {
          card.classList.add("llw-data-card");
          card.dataset.llwNumber = String(index + 1).padStart(2, "0");
        });
      }

      if (key.startsWith("2.")) {
        const grid = directChildren(section, "div")[0];
        grid?.classList.add("llw-rating-grid");
        [...(grid?.children || [])].forEach((card) => {
          const title = card.querySelector("strong")?.textContent || "";
          card.classList.add("llw-rating-card", `llw-rating-card--${ratingTone(title)}`);
        });
      }

      if (key.startsWith("3.")) {
        const hasAllergiker = [...section.querySelectorAll("summary strong")].some(
          (element) => normalize(element.textContent) === "allergiker"
        );
        if (!hasAllergiker) {
          const detail = document.createElement("details");
          detail.innerHTML = `
            <summary><strong>Allergiker</strong></summary>
            <div>
              <p><strong>Wir betrachten:</strong> das konkrete Allergen oder die tatsächlich bekannte Unverträglichkeit. Allgemeine Chargendaten allein reichen dafür nicht.</p>
              <p><strong>Wichtig:</strong> Staubarmut kann separat bewertet werden, ersetzt aber nicht die Kenntnis des individuellen Auslösers.</p>
              <p><strong>Grenze:</strong> Deshalb vergeben wir keine pauschale automatische Allergiker-Eignung.</p>
              <p><a href="/pages/futterlexikon#kategorie_allergiker">„Allergiker“ im Lexikon nachlesen →</a></p>
            </div>
          `;
          section.append(detail);
        }

        const details = directChildren(section, "details");
        if (details.length) {
          const list = document.createElement("div");
          list.className = "llw-criteria-list";
          section.insertBefore(list, details[0]);
          details.forEach((detail) => list.append(detail));
        }
      }

      if (key.startsWith("4.")) {
        const grid = directChildren(section, "div")[0];
        grid?.classList.add("llw-process-grid");
        [...(grid?.children || [])].forEach((card, index) => {
          card.classList.add("llw-process-card");
          card.dataset.llwStep = `SCHRITT ${String(index + 1).padStart(2, "0")}`;
        });
      }

      if (key.startsWith("5.")) section.classList.add("llw-topic--approval");

      if (key.startsWith("6.")) {
        const callout = directChildren(section, "div")[0];
        callout?.classList.add("llw-soft-callout");
      }
    }

    if (sections[0] && !body.querySelector(".llw-reading-guide")) {
      const readingGuide = document.createElement("section");
      readingGuide.className = "llw-topic llw-reading-guide";
      readingGuide.id = "llw-2-daten-richtig-lesen";
      readingGuide.innerHTML = `
        <h2>2. Daten richtig lesen</h2>
        <p>Diese Angaben entscheiden darüber, ob sich ein Messwert vergleichen und fachlich einordnen lässt.</p>
        <div class="llw-reading-grid">
          <a href="/pages/futterlexikon#bezugsbasis"><strong>Bezugsbasis</strong><p>Trockensubstanz und Originalsubstanz dürfen nicht ungeprüft miteinander verglichen werden.</p></a>
          <a href="/pages/futterlexikon#messwert_operator"><strong>Messgrenze</strong><p>„&lt;“ bedeutet unterhalb der angegebenen Grenze – nicht automatisch null.</p></a>
          <a href="/pages/futterlexikon#nicht_untersucht"><strong>Nicht untersucht</strong><p>Der Wert wurde nicht bestimmt. Das ist etwas anderes als „nicht dokumentiert“.</p></a>
          <a href="/pages/futterlexikon#repraesentativitaet"><strong>Repräsentativität</strong><p>Eine Probe soll die Charge möglichst gut abbilden; sie untersucht nicht jeden einzelnen Ballen.</p></a>
          <a href="/pages/futterlexikon#messunsicherheit"><strong>Messunsicherheit</strong><p>Laborwerte besitzen einen methodischen Unsicherheitsbereich und sind keine mathematisch exakten Naturkonstanten.</p></a>
          <a href="/pages/futterlexikon#freigabestatus"><strong>Freigabestatus</strong><p>Ein vorhandener Datensatz wird erst nach der vorgesehenen Prüfung öffentlich verwendet.</p></a>
        </div>
      `;
      sections[0].insertAdjacentElement("afterend", readingGuide);
      sections.splice(1, 0, readingGuide);
    }

    const numberedHeadings = [
      "1. Welche Daten fließen in die Bewertung ein?",
      "2. Daten richtig lesen",
      "3. Was bedeuten unsere Bewertungen?",
      "4. So betrachten wir die einzelnen Fütterungsziele",
      "5. Erst Datenprüfung, dann Bewertung",
      "6. Automatisch vorgeschlagen – manuell freigegeben",
      "7. Was unsere Einordnung nicht ist"
    ];
    numberedHeadings.forEach((label, index) => {
      const heading = sections[index]?.querySelector(":scope > h2");
      if (heading) heading.textContent = label;
    });

    addTopicNavigation(body, sections, hero);

    const actions = [...body.querySelectorAll("div")].find((element) => {
      const directLinks = directChildren(element, "a");
      return (
        !element.classList.contains("llw-legacy-nav") &&
        directLinks.length === 2 &&
        directLinks.some((link) => link.href.includes("/pages/futterlexikon"))
      );
    });
    actions?.classList.add("llw-actions");

    const finalParagraph = [...body.querySelectorAll("p")].at(-1);
    if (finalParagraph && normalize(finalParagraph.textContent).startsWith("fachlicher hinweis")) {
      finalParagraph.classList.add("llw-note");
    }
  };

  const init = () => {
    const root = document.querySelector("[data-llw-page]");
    if (!root || root.dataset.llwEnhanced === "true") return;
    const content = root.querySelector("[data-llw-content]");
    const body = content?.firstElementChild;
    if (!body) return;

    root.dataset.llwEnhanced = "true";
    body.classList.add("llw-body");

    switch (root.dataset.llwPage) {
      case "futterwissen":
        enhanceFutterwissen(body);
        break;
      case "futterlexikon":
        enhanceLexicon(body);
        break;
      case "lollenstein-kriterien":
        enhanceCriteria(body);
        break;
      default:
        break;
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", init);
})();
